import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';

export type { CountryCode };

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'MA';

let phoneCountrySet: Set<string> | null = null;

function knownPhoneCountries(): Set<string> {
  if (!phoneCountrySet) phoneCountrySet = new Set(getCountries());
  return phoneCountrySet;
}

/** ISO-2 calling-code country. Unknown or empty falls back to Morocco. */
export function asPhoneCountry(code: string | null | undefined): CountryCode {
  const t = code?.trim().toUpperCase() ?? '';
  if (t && knownPhoneCountries().has(t)) return t as CountryCode;
  return DEFAULT_PHONE_COUNTRY;
}

export type NormalizePhoneResult =
  | { e164: string }
  | { error: 'empty' }
  | { error: 'invalid' };

export type PhoneCountryOption = {
  value: CountryCode;
  label: string;
  dialCode: string;
};

const phoneCountryOptionsCache = new Map<string, PhoneCountryOption[]>();

function regionLabel(code: CountryCode, locale: string): string {
  try {
    if (typeof Intl === 'undefined' || !Intl.DisplayNames) return code;
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

export function formatNationalDigits(digits: string, country: CountryCode): string {
  if (!digits) return '';
  const at = new AsYouType(country);
  for (const d of digits) {
    at.input(d);
  }
  return at.getChars();
}

export function buildE164FromNationalDisplay(
  nationalDisplay: string,
  country: CountryCode,
): string {
  const digits = digitsOnly(nationalDisplay);
  if (!digits) return '';
  const at = new AsYouType(country);
  for (const d of digits) {
    at.input(d);
  }
  const num = at.getNumber();
  if (num?.isValid()) return num.number;
  try {
    const p = parsePhoneNumber(nationalDisplay, country);
    if (p?.isValid()) return p.number;
  } catch {
    /* ignore */
  }
  return '';
}

export function applyInternationalTypedInput(
  raw: string,
  fallbackCountry: CountryCode,
): {
  country: CountryCode;
  nationalDisplay: string;
  e164: string;
} | null {
  const t = raw.trim();
  if (!t.startsWith('+') && !t.startsWith('00')) return null;

  const normalized =
    t.startsWith('00')
      ? `+${digitsOnly(t.slice(2))}`
      : `+${digitsOnly(t.startsWith('+') ? t.slice(1) : t)}`;
  if (normalized.length < 2) {
    return {
      country: fallbackCountry,
      nationalDisplay: raw,
      e164: '',
    };
  }

  const at = new AsYouType();
  for (let i = 0; i < normalized.length; i++) {
    const c = normalized[i];
    if (c === '+') at.input('+');
    else if (/\d/.test(c)) at.input(c);
  }

  const detected = at.getCountry() as CountryCode | undefined;
  const fullDigits = digitsOnly(normalized);
  const numFromIntl = at.getNumber();

  if (detected) {
    const cc = getCountryCallingCode(detected);
    let nationalDigits =
      numFromIntl?.nationalNumber != null ? String(numFromIntl.nationalNumber) : '';
    if (!nationalDigits) {
      nationalDigits = fullDigits.startsWith(cc) ? fullDigits.slice(cc.length) : fullDigits;
    }
    const nationalDisplay = formatNationalDigits(nationalDigits, detected);
    const e164 =
      numFromIntl?.isValid() === true
        ? numFromIntl.number
        : buildE164FromNationalDisplay(nationalDisplay, detected);
    return {
      country: detected,
      nationalDisplay,
      e164,
    };
  }

  return {
    country: fallbackCountry,
    nationalDisplay: at.getChars() || raw,
    e164: '',
  };
}

/**
 * Parse raw input to E.164 when valid. Empty input → `{ error: 'empty' }`.
 */
export function normalizePhoneToE164(
  raw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): NormalizePhoneResult {
  const t = typeof raw === 'string' ? raw.trim() : '';
  if (!t) return { error: 'empty' };

  try {
    const intl = applyInternationalTypedInput(t, defaultCountry);
    if (intl?.e164) return { e164: intl.e164 };

    const p = t.startsWith('+')
      ? parsePhoneNumber(t)
      : parsePhoneNumber(t, defaultCountry);
    if (p?.isValid()) return { e164: p.number };
  } catch {
    /* fall through */
  }

  const e164 = buildE164FromNationalDisplay(t, defaultCountry);
  if (e164) return { e164 };

  return { error: 'invalid' };
}

export function isValidE164Phone(value: string | null | undefined): boolean {
  const t = typeof value === 'string' ? value.trim() : '';
  if (!t) return false;
  try {
    const p = parsePhoneNumber(t);
    return p.isValid();
  } catch {
    return false;
  }
}

/**
 * Display helper: international format when parseable, else trimmed raw.
 */
export function formatPhoneForDisplay(
  e164OrRaw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): string {
  const t = typeof e164OrRaw === 'string' ? e164OrRaw.trim() : '';
  if (!t) return '—';
  try {
    const p = t.startsWith('+')
      ? parsePhoneNumber(t)
      : parsePhoneNumber(t, defaultCountry);
    if (p?.isValid()) return p.formatInternational();
  } catch {
    /* ignore */
  }
  return t;
}

/**
 * Convention / PDF bake format: `+CC-xxx-……`
 * (`+` country calling code, hyphen, national in 3+rest groups when long enough).
 * Falls back to trimmed raw when the number is not valid.
 */
export function formatPhoneForDocument(
  e164OrRaw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): string {
  const raw = typeof e164OrRaw === 'string' ? e164OrRaw.trim() : '';
  if (!raw) return '';
  const norm = normalizePhoneToE164(raw, defaultCountry);
  if (!('e164' in norm)) return raw;
  try {
    const p = parsePhoneNumber(norm.e164);
    if (!p?.isValid()) return norm.e164;
    const cc = String(p.countryCallingCode);
    const national = String(p.nationalNumber ?? '');
    if (!national) return `+${cc}`;
    if (national.length >= 9) {
      return `+${cc}-${national.slice(0, 3)}-${national.slice(3)}`;
    }
    return `+${cc}-${national}`;
  } catch {
    return norm.e164;
  }
}

/** Label / meta used for Phone / WhatsApp notice fields on conventions. */
export function isSigningPhoneFieldLabel(label: string | null | undefined): boolean {
  const n = (label ?? '').trim().toLowerCase();
  if (!n) return false;
  if (n === 'phone' || n === 'phone / whatsapp' || n === 'téléphone' || n === 'telephone') {
    return true;
  }
  return n.includes('whatsapp') || n.includes('téléphone') || n.includes('telephone');
}

export type PhoneDisplayParts = {
  country: CountryCode;
  dialCode: string;
  national: string;
};

/** Flag + muted dial code + national digits for UI (tables, cards). */
export function formatPhoneDisplayParts(
  e164OrRaw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): PhoneDisplayParts | null {
  const t = typeof e164OrRaw === 'string' ? e164OrRaw.trim() : '';
  if (!t) return null;
  try {
    const p = t.startsWith('+')
      ? parsePhoneNumber(t)
      : parsePhoneNumber(t, defaultCountry);
    if (p?.isValid() && p.country) {
      return {
        country: p.country,
        dialCode: `+${p.countryCallingCode}`,
        national: formatNationalDigits(p.nationalNumber ?? '', p.country),
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function countryCodeToFlagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

/** Country picker labels. Default English (signing UI + shared PhoneInput). */
export function getPhoneCountryOptions(locale: string = 'en'): PhoneCountryOption[] {
  const key = locale.trim().toLowerCase() || 'en';
  const cached = phoneCountryOptionsCache.get(key);
  if (cached) return cached;

  const rows: PhoneCountryOption[] = [];
  for (const code of getCountries()) {
    try {
      const dialCode = String(getCountryCallingCode(code));
      const name = regionLabel(code, key);
      rows.push({
        value: code,
        dialCode,
        label: `${name} (+${dialCode})`,
      });
    } catch {
      /* skip unsupported */
    }
  }
  rows.sort((a, b) => a.label.localeCompare(b.label, key));

  phoneCountryOptionsCache.set(key, rows);
  return rows;
}

export function parseE164ToNationalParts(
  e164: string,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): { country: CountryCode; nationalDisplay: string } {
  try {
    const p = parsePhoneNumber(e164);
    if (p?.country) {
      return {
        country: p.country,
        nationalDisplay: formatNationalDigits(p.nationalNumber ?? '', p.country),
      };
    }
  } catch {
    /* ignore */
  }
  return {
    country: fallbackCountry,
    nationalDisplay: formatNationalDigits(digitsOnly(e164), fallbackCountry),
  };
}
